import React, { memo, useState, useEffect, useRef, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, Brush, CartesianGrid } from 'recharts';
import EntryPanel from './EntryPanel'
import BecomePremiumInvite from './BecomePremiumInvite'
import './ChartIndicators.css';
import { CsrfTokenContext } from '../CsrfTokenContext';
import { isTokenExpired, handleLogin } from '../pages/utils/AuthenticationHelper';
import { setRsi_chart, setStoch_chart, setBoll_chart, setMacd_chart, setEma7,setEma9, setEma12, setEma14, setEma21, setEma50, setEma200 } from '../pages/slices/indicatorsPlotSlicer';
    
const ChartIndicators = memo(() => {
  const { selectedSymbol, interval, startDate, endDate, daysPreSet } = useSelector(state => state.entryPanel);
  const { rsi_chart, stoch_chart, boll_chart, macd_chart, ema_7, ema_9, ema_12, ema_14, ema_21, ema_50, ema_200 } = useSelector(state => state.indicatorsPlot);
  const { isPremium } = useSelector(state => state.globalData);
  
  
  // const [activeIndicator, setActiveIndicator] = useState(''); // Initialize with '' to disable all by default
  // const [visibleLines, setVisibleLines] = useState(new Array(7).fill(false)); // Initialize with 7 EMA indicators
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [brushStartIndex, setBrushStartIndex] = useState(0);
  const [brushEndIndex, setBrushEndIndex] = useState(data.length - 1);
  const csrfToken = useContext(CsrfTokenContext);

  const plotRef = useRef(null);
  const dispatch = useDispatch();  

  const totalDataPoints = data.length;
  const xAxisInterval = totalDataPoints > 5 ? Math.floor(totalDataPoints / 5) : 0;

  const toggleButton = (id) => {    
    switch (id) {
      case 0:        
        dispatch(setEma7(!ema_7));        
        break;
      case 1:
        dispatch(setEma9(!ema_9));        
        break;
      case 2:
        dispatch(setEma12(!ema_12));        
        break;
      case 3:
        dispatch(setEma14(!ema_14));        
        break;
      case 4:
        dispatch(setEma21(!ema_21));        
        break;
      case 5:
        dispatch(setEma50(!ema_50));        
        break;
      case 6:
        dispatch(setEma200(!ema_200));
        break;
      case 7:
        dispatch(setRsi_chart(!rsi_chart));
        break;
      case 8:
        dispatch(setStoch_chart(!stoch_chart));
        break;
      case 9:
        dispatch(setBoll_chart(!boll_chart));
        break;
      case 10:
        dispatch(setMacd_chart(!macd_chart));        
        break;    
      default:
        break;
    }
  };

  const handlePlotError = (pltError) => {
    setLoading(false);
    console.log(pltError);
  };

  // *********** FUNCTIONS ***********

  const isChartVisible = (id) => {
    switch (id) {
      case 0:
        return ema_7;        
      case 1:
        return ema_9;        
      case 2:
        return ema_12;        
      case 3:
        return ema_14;        
      case 4:
        return ema_21;        
      case 5:
        return ema_50;        
      case 6:
        return ema_200;        
      case 7:
        return rsi_chart;        
      case 8:
        return stoch_chart;        
      case 9:
        return boll_chart;
      case 10:
        return macd_chart;        
      default:
        break;
    }
  };

  const getColor = (id) => {
    switch (id) {
      case 0:               
        return 'checked_0';        
      case 1:             
        return 'checked_1';        
      case 2:               
        return 'checked_2';        
      case 3:               
        return 'checked_3';        
      case 4:               
        return 'checked_4';        
      case 5:             
        return 'checked_5';        
      case 6:             
        return 'checked_6';        
      case 7:            
        return 'checked_7';        
      case 8:          
        return 'checked_8';        
      case 9:       
        return 'checked_9';
      case 10:         
        return 'checked_10';        
      default:
        break;
    }
  };

  const hookParts = (hookTo) => {
    setTimeout(() => {
        if (hookTo.current) {
            window.scrollTo({
                top: hookTo.current.offsetTop,
                behavior: 'smooth'
            });
        }
    }, 300);
  };

  // const normalizeData = (data, keys) => {
  //   keys.forEach(key => {
  //     const values = data.map(d => d[key]);
  //     const min = Math.min(...values);
  //     const max = Math.max(...values);
  //     data = data.map(d => ({
  //       ...d,
  //       [key]: (d[key] - min) / (max - min),
  //     }));
  //   });
  //   return data;
  // };

  const formatDate = (date) => {
    const result = new Date(date);
    const year = result.getFullYear();
    const month = String(result.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const day = String(result.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const addDays = (date, days) => {
    const result = new Date(date); // Create a new Date object based on the given date
    result.setDate(result.getDate() + days); // Add the specified number of days
    return result;
  };
    
  useEffect(() => {

    const refreshTokenIfNeeded = async (symbol, interval) => {
      if (isTokenExpired()) {        
        await handleLogin(csrfToken); // Refresh the token if expired
      }
      plotIndicators(symbol, interval);
    };

    const plotIndicators = async (symbol, interval) => {
      try {
        setLoading(true);

        let sDate = '';
        let eDate = '';

        if(startDate!=='') sDate = formatDate(startDate);        
        if(endDate!=='') eDate = formatDate(addDays(endDate, 1));
        
        const strFetch = `/api/plot_indicators?symbol=${symbol}&interval=${interval}&days=${daysPreSet}&sDate=${sDate}&eDate=${eDate}`;
          
        fetch(strFetch, {
          method: 'GET',       
          headers: {            
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken
          }
      }).then(async response => {
            if (!response.ok) {
              const errorData = await response.json();
              handlePlotError(errorData.error);
              throw new Error(errorData.error);
            }
            const result = await response.json();            
            setData(result);
          }).then(()=>setLoading(false)).then(hookParts(plotRef)).catch(error => handlePlotError(error));

  
      } catch (err) {
        console.error('Error plotting data:', err);
        setLoading(false);
        handlePlotError(err);
      }
    };

    if(interval && Object.keys(selectedSymbol).length !== 0 && (daysPreSet || endDate)){
      hookParts(plotRef);
      refreshTokenIfNeeded(selectedSymbol.value, interval);
    }    

  }, [selectedSymbol, interval, daysPreSet, startDate, endDate, csrfToken]);

  useEffect(()=>{
    setBrushStartIndex(0);
    setBrushEndIndex(data.length - 1);
  },[data]);
  
  const indicators = ['Ema 7', 'Ema 9', 'Ema 12', 'Ema 14', 'Ema 21', 'Ema 50', 'Ema 200', 'RSI', 'Stochastic', 'Bollinger', 'MACD'];
  
  // Custom tooltip for each chart
  const renderCustomTooltip = (props) => {
    const { active, payload } = props;
  
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip" style={{textAlignLast:'left'}}>
          <p>{`Date: ${payload[0].payload.date_time}`}</p>
          {payload.map((entry, index) => (            
            <p key={index} style={{ color: entry.color, textShadow:'-1px -1px 0 #131722, 1px -1px 0 #131722, -1px 1px 0 #131722, 1px 1px 0 #131722' }}>
              {`${entry.name}: ${entry.name === 'Price' ? entry.payload.close_org.toFixed(7) : entry.value.toFixed(7)}`}
            </p>
          ))}
        </div>
      );
    }
  
    return null;
  };

  const formatYAxis = (tick) => {
    return tick.toFixed(2);
  };

  

  const handleBrushChange = ({ startIndex, endIndex }) => {
    setBrushStartIndex(startIndex);
    setBrushEndIndex(endIndex);
  };

  const filteredData = data.slice(brushStartIndex, brushEndIndex + 1);

  const minY = Math.min(...filteredData.map(d => Math.min(d.seriesA, d.seriesB, d.seriesC)));
  const maxY = Math.max(...filteredData.map(d => Math.max(d.seriesA, d.seriesB, d.seriesC)));
  const adjustedMinY = minY - 10 < 0 ? 0 : minY - 10; // Ensure the minimum Y is not less than 0
  const adjustedMaxY = maxY + 10; // Adjust as needed  

  const minRsiY = Math.min(...filteredData.map(d => Math.min(d.rsi)));
  const maxRsiY = Math.max(...filteredData.map(d => Math.max(d.rsi)));
  const adjustedRsiMinY = minRsiY - 10 < 0 ? 0 : minY - 10;
  const adjustedRsiMaxY = maxRsiY + 10;

  const minStochY = Math.min(...filteredData.map(d => Math.min(d.stochrsi_k)));
  const maxStochY = Math.max(...filteredData.map(d => Math.max(d.stochrsi_k)));
  const adjustedStochMinY = minStochY - 10 < 0 ? 0 : minY - 10;
  const adjustedStochMaxY = maxStochY + 10;

  const minMacdY = Math.min(...filteredData.map(d => Math.min(d.macd)));
  const maxMacdY = Math.max(...filteredData.map(d => Math.max(d.macd)));
  const adjustedMacdMinY = minMacdY - 10 < 0 ? 0 : minY - 10;
  const adjustedMacdMaxY = maxMacdY + 10;

  return (
    <><EntryPanel />    
    <div style={{ height: '500px' }}>      
        <div className={`checkbox-container ${!(interval && Object.keys(selectedSymbol).length !== 0 && (endDate || daysPreSet > 0)) ? 'disabled_div' : ''}`}>
          <fieldset className='fieldset_indicator'>
            <legend className='legend'>Indicators</legend>
            <div className="line-indicators">           
            </div>
            <div className="line-indicators">
              {indicators.map((chart, index) => (
                <div
                  key={index}
                  className={`checkbox ${isChartVisible(index) ? getColor(index) : ''}`}
                  onClick={() => {
                    toggleButton(index);
                  } }
                >
                  {chart}
                </div>
              ))}
            </div>
            </fieldset>
        </div>

        {!isPremium && data.length > 0 &&(
          <div style={{paddingBottom: '35px'}}>
            <BecomePremiumInvite/>
          </div>
        )}
      
      <div ref={plotRef} style={{paddingBottom: '60px'}}>        
        {Object.keys(selectedSymbol).length !== 0 &&
          <span className='centered_text'>{selectedSymbol.value}</span>}
          {/* Price and EMA Indicators */}
          {data.length > 0 && (        
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={data} syncId="price">
                <XAxis dataKey="date_time"
                  interval={xAxisInterval}
                  angle={0}
                  tick={{ fontSize: 12 }} />                
                <YAxis domain={[adjustedMinY, adjustedMaxY]} 
                tick={{ fontSize: 13 }}
                hide={false} tickFormatter={formatYAxis}/>
                <Tooltip content={renderCustomTooltip} />
                <Legend />
                <Line key='4' type="monotone" dot={false} dataKey="close" stroke={!loading?"#5dff00":"white"} name='Price' />
                {ema_7 && <Line key='10' type="monotone" dot={false} dataKey="ema_7" stroke={!loading?"#82ca9d":"white"} name='Ema 7' />}
                {ema_9 && <Line key='11' type="monotone" dot={false} dataKey="ema_9" stroke={!loading?"#ff7300":"white"} name='Ema 9' />}
                {ema_12 && <Line key='12' type="monotone" dot={false} dataKey="ema_12" stroke={!loading?"#387908":"white"} name='Ema 12' />}
                {ema_14 && <Line key='13' type="monotone" dot={false} dataKey="ema_14" stroke={!loading?"#001fff":"white"} name='Ema 14' />}
                {ema_21 && <Line key='14' type="monotone" dot={false} dataKey="ema_21" stroke={!loading?"#aa00ff":"white"} name='Ema 21' />}
                {ema_50 && <Line key='15' type="monotone" dot={false} dataKey="ema_50" stroke={!loading?"#8884d8":"white"} name='Ema 50' />}
                {ema_200 && <Line key='16' type="monotone" dot={false} dataKey="ema_200" stroke={!loading?"#ffffff":"white"} name='Ema 200' />}

                {boll_chart &&  
                  <>
                  <Line key='5' type="monotone" dot={false} dataKey="sma" stroke={!loading ? "#8884a8" : "white"} name='SMA' />
                  <Line key='6' type="monotone" dot={false} dataKey="upper" stroke={!loading ? "#ff0022" : "white"} name='Upper' />
                  <Line key='7' type="monotone" dot={false} dataKey="lower" stroke={!loading ? "#ffca00" : "white"} name='Lower' /></>
                }

                <CartesianGrid stroke="#494949" horizontal={true} vertical={false} />
                <Brush
                  dataKey="date_time"
                  height={20}
                  stroke="#8884d8"
                  fill="#f5f5f500"
                  travellerWidth={10}
                  onChange={handleBrushChange}
                  gap={5} />
              </LineChart>
            </ResponsiveContainer>
          )}

          {/* RSI */}        
          {rsi_chart && (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={filteredData} syncId="price">
                <XAxis dataKey="date_time"
                  interval={xAxisInterval}
                  angle={0}
                  tick={{ fontSize: 12 }} />
                <YAxis 
                  domain={[adjustedRsiMinY, adjustedRsiMaxY]}                   
                  hide={false} tickFormatter={formatYAxis} />
                <Tooltip content={renderCustomTooltip} />
                <Legend />
                <Line key='1' type="monotone" dot={false} dataKey="rsi" stroke={!loading?"#d287ff":"white"} name='RSI' />              
                <Line key='10' type="monotone" dot={false} dataKey="rsi_ma" stroke={!loading?"#f9c134":"white"} name='RSI_MA' />              
                <CartesianGrid stroke="#494949" horizontal={true} vertical={false} />
              </LineChart>
            </ResponsiveContainer>          
          )}

          {/* Stochastic */}
          {stoch_chart && (
            
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={filteredData} syncId="price">
                <XAxis dataKey="date_time"
                  interval={xAxisInterval}
                  angle={0}
                  tick={{ fontSize: 12 }} />
                <YAxis domain={[adjustedStochMinY, adjustedStochMaxY]} hide={false} tickFormatter={formatYAxis} />
                <Tooltip content={renderCustomTooltip} />
                <Legend />
                <Line key='2' type="monotone" dot={false} dataKey="stochrsi_k" stroke={!loading ? "#ff7f00" : "white"} name='Stoch K' />
                <Line key='3' type="monotone" dot={false} dataKey="stochrsi_d" stroke={!loading ? "#8804d8" : "white"} name='Stoch D' />
                <CartesianGrid stroke="#494949" horizontal={true} vertical={false} />                  
              </LineChart>
            </ResponsiveContainer>              
          )}

          {/* MACD */}        
          {macd_chart && (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={filteredData} syncId="price">
                <XAxis dataKey="date_time"
                  interval={xAxisInterval}
                  angle={0}
                  tick={{ fontSize: 12 }} />
                <YAxis domain={[adjustedMacdMinY, adjustedMacdMaxY]} hide={false} tickFormatter={formatYAxis} />
                <Tooltip content={renderCustomTooltip} />
                <Legend />
                <Line key='8' type="monotone" dot={false} dataKey="macd" stroke={!loading ? "#ff00b3" : "white"} name='MACD' />
                <Line key='9' type="monotone" dot={false} dataKey="macd_signal" stroke={!loading ? "#8854d8" : "white"} name='MACD Sig' />
                <CartesianGrid stroke="#494949" horizontal={true} vertical={false} />                  
              </LineChart>
            </ResponsiveContainer>          
          )}
      </div>
    </div></>
  );
});

export default ChartIndicators;